import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3AssessmentPageState from './useTreatmentPlanV3AssessmentPageState';

import type { AppDispatch } from '../../../../../../store';
import type {
  AssessmentReadinessItemId,
  TreatmentPlanV3AssessmentReadinessItemPayload,
} from '../../../../../../store/treatmentPlanV3';
import type {
  TreatmentPlanV3AssessmentReadinessDependencies,
  UseTreatmentPlanV3AssessmentReadinessResult,
} from './types';

export default function useTreatmentPlanV3AssessmentReadiness() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const { isSavingReadiness } = useTreatmentPlanV3AssessmentPageState();

  return useTreatmentPlanV3AssessmentReadinessWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    isSaving: isSavingReadiness,
  });
}

export function useTreatmentPlanV3AssessmentReadinessWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  isSaving,
}: TreatmentPlanV3AssessmentReadinessDependencies): UseTreatmentPlanV3AssessmentReadinessResult {
  const updateItem = useCallback(
    async (item: AssessmentReadinessItemId, data: TreatmentPlanV3AssessmentReadinessItemPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3AssessmentReadinessItem({
          treatmentPlanId,
          item,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3AssessmentReadinessItem.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    isSaving,
    updateItem,
  };
}
